import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../api/client';
import {
  PRIMARY,
  TEXT_DARK,
  TEXT_MUTED,
  TEXT_SECONDARY,
  BORDER_LIGHT,
  BG_CARD,
  BG_SURFACE,
} from './table-management/designTokens';

interface MatchSignal {
  type: string;
  weight: number;
  details: string;
}

interface MatchSuggestion {
  id: string;
  guestRecordId: string;
  guestName: string;
  memberId: string;
  memberName: string;
  memberEmail: string;
  confidence: number;
  signals: MatchSignal[];
  status: string;
  createdAt: string;
}

interface MatchSuggestionsPanelProps {
  eventId: string;
  refreshTrigger?: number;
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    backgroundColor: BG_CARD,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1rem',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: '0 0 0.75rem 0',
  },
  description: {
    fontSize: '0.85rem',
    color: TEXT_MUTED,
    marginBottom: '1rem',
  },
  loadingText: {
    textAlign: 'center' as const,
    padding: '1.5rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  errorMsg: {
    color: '#d32f2f',
    backgroundColor: '#fdecea',
    padding: '0.5rem 0.75rem',
    borderRadius: '4px',
    fontSize: '0.85rem',
    marginBottom: '0.75rem',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem 1rem',
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.75rem',
  },
  card: {
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '6px',
    padding: '1rem',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '0.5rem',
  },
  cardNames: {
    flex: 1,
  },
  guestName: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: TEXT_DARK,
  },
  arrow: {
    fontSize: '0.8rem',
    color: TEXT_MUTED,
    margin: '0.15rem 0',
  },
  memberName: {
    fontSize: '0.9rem',
    fontWeight: 500,
    color: TEXT_DARK,
  },
  memberEmail: {
    fontSize: '0.8rem',
    color: TEXT_SECONDARY,
  },
  confidenceBadge: {
    padding: '0.25rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  signalsList: {
    marginTop: '0.5rem',
    marginBottom: '0.75rem',
  },
  signalItem: {
    fontSize: '0.8rem',
    color: TEXT_SECONDARY,
    padding: '0.15rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
  },
  signalType: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    backgroundColor: '#eef1ef',
    padding: '0.1rem 0.4rem',
    borderRadius: '3px',
  },
  cardActions: {
    display: 'flex',
    gap: '0.5rem',
    justifyContent: 'flex-end',
  },
  approveBtn: {
    padding: '0.4rem 0.8rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  rejectBtn: {
    padding: '0.4rem 0.8rem',
    backgroundColor: '#fff',
    color: '#d32f2f',
    border: '1px solid #d32f2f',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  disabledBtn: {
    padding: '0.4rem 0.8rem',
    backgroundColor: '#ccc',
    color: '#666',
    border: 'none',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 600,
    cursor: 'not-allowed',
  },
};

function getConfidenceStyle(confidence: number): React.CSSProperties {
  if (confidence >= 0.85) {
    return { backgroundColor: '#e8f5e9', color: '#2e7d32' };
  } else if (confidence >= 0.7) {
    return { backgroundColor: '#fff3e0', color: '#e65100' };
  }
  return { backgroundColor: '#fce4ec', color: '#c62828' };
}

export function MatchSuggestionsPanel({ eventId, refreshTrigger }: MatchSuggestionsPanelProps) {
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<Record<string, boolean>>({});

  const fetchSuggestions = useCallback(async () => {
    try {
      setError('');
      const data = await apiClient<MatchSuggestion[]>(
        `/guest-accounts/suggestions/${eventId}`,
      );
      setSuggestions(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load match suggestions.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions, refreshTrigger]);

  // handleApprove disabled — using ReadyToLink workflow via K-LOC instead

  async function handleStageForLinking(suggestion: MatchSuggestion) {
    setActionLoading((prev) => ({ ...prev, [suggestion.id]: true }));
    setError('');

    try {
      await apiClient('/guest-accounts/stage-for-linking', {
        method: 'POST',
        body: { suggestionId: suggestion.id },
      });
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to stage suggestion for linking.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [suggestion.id]: false }));
    }
  }

  async function handleReject(suggestion: MatchSuggestion) {
    setActionLoading((prev) => ({ ...prev, [suggestion.id]: true }));
    setError('');

    try {
      await apiClient('/guest-accounts/reject', {
        method: 'POST',
        body: { suggestionId: suggestion.id },
      });
      setSuggestions((prev) => prev.filter((s) => s.id !== suggestion.id));
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to reject suggestion.');
    } finally {
      setActionLoading((prev) => ({ ...prev, [suggestion.id]: false }));
    }
  }

  if (loading) {
    return (
      <div style={styles.container}>
        <h3 style={styles.title}>Match Suggestions</h3>
        <p style={styles.loadingText}>Loading suggestions...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>Match Suggestions</h3>
      <p style={styles.description}>
        Review potential matches between guest records and existing members. Approve to link accounts or reject to dismiss.
      </p>

      {/* Summary Banner */}
      {suggestions.length > 0 && (
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', fontSize: '0.8rem', color: TEXT_MUTED }}>
          <span><strong>{suggestions.length}</strong> total</span>
          <span style={{ color: '#2e7d32' }}>✅ <strong>{suggestions.filter((s) => s.status === 'Approved').length}</strong> linked</span>
          <span style={{ color: '#e65100' }}>⏳ <strong>{suggestions.filter((s) => s.status === 'Pending').length}</strong> pending</span>
        </div>
      )}

      {error && <div style={styles.errorMsg}>{error}</div>}

      {suggestions.length === 0 ? (
        <div style={styles.emptyState}>
          No match suggestions. Run provisioning to detect potential member matches.
        </div>
      ) : (
        <div style={styles.cardList}>
          {suggestions.map((suggestion) => {
            const isProcessing = actionLoading[suggestion.id] || false;
            const confidencePercent = Math.round(suggestion.confidence * 100);
            const isLinked = suggestion.status === 'Approved';

            return (
              <div key={suggestion.id} style={{ ...styles.card, ...(isLinked ? { borderLeft: '3px solid #2e7d32', opacity: 0.85 } : {}) }}>
                <div style={styles.cardHeader}>
                  <div style={styles.cardNames}>
                    <div style={styles.guestName}>
                      {isLinked && <span style={{ color: '#2e7d32', marginRight: '0.4rem' }}>✅</span>}
                      {suggestion.guestName}
                    </div>
                    <div style={styles.arrow}>{isLinked ? '→ linked to' : '→ matches'}</div>
                    <div style={styles.memberName}>{suggestion.memberName}</div>
                    <div style={styles.memberEmail}>{suggestion.memberEmail}</div>
                  </div>
                  {isLinked ? (
                    <span style={{ ...styles.confidenceBadge, backgroundColor: '#e8f5e9', color: '#2e7d32' }}>Linked</span>
                  ) : (
                    <span style={{ ...styles.confidenceBadge, ...getConfidenceStyle(suggestion.confidence) }}>
                      {confidencePercent}%
                    </span>
                  )}
                </div>

                {!isLinked && suggestion.signals && suggestion.signals.length > 0 && (
                  <div style={styles.signalsList}>
                    {suggestion.signals.map((signal, i) => (
                      <div key={i} style={styles.signalItem}>
                        <span style={styles.signalType}>{signal.type.replace(/_/g, ' ')}</span>
                        <span>{signal.details}</span>
                      </div>
                    ))}
                  </div>
                )}

                {!isLinked && (
                  <div style={styles.cardActions}>
                    <button
                      style={isProcessing ? styles.disabledBtn : styles.rejectBtn}
                      onClick={() => handleReject(suggestion)}
                      disabled={isProcessing}
                      aria-label={`Reject match for ${suggestion.guestName}`}
                    >
                      Reject
                    </button>
                    <button
                      style={isProcessing ? styles.disabledBtn : { ...styles.approveBtn, backgroundColor: '#1565c0' }}
                      onClick={() => handleStageForLinking(suggestion)}
                      disabled={isProcessing}
                      aria-label={`Stage ${suggestion.guestName} for linking in K-LOC`}
                    >
                      🔗 Ready to Link
                    </button>
                    <button
                      style={styles.disabledBtn}
                      disabled
                      title="Use 'Ready to Link' to send to K-LOC for safe linking"
                      aria-label="Approve disabled — use Ready to Link instead"
                    >
                      Approve
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
